// modules/work/projects/jobs/context/job-context.tsx

import React, { createContext, useContext } from "react";
import {
  fetchJobBillablesCount,
  fetchJobMeta,
  fetchJobMilestoneCount,
} from "@/modules/work/jobs/api";
import { fetchJobDepartment } from "./employees/api";

import { billableKeys } from "components/billable/queries";
import { fetchBucketJobConfig } from "./config/bucket/read";
import { fetchDedicatedJobConfig } from "./config/dedicated/read";
import { fetchFixedJobConfig } from "./config/fixed/read";
import { fetchHourlyJobConfig } from "./config/hourly/read";
import { fetchRecurringJobConfig } from "./config/recurring/read";
import { milestoneKeys } from "components/milestone/queries";
import { usePermissions } from "contexts/permission";
import { useFetch } from "@/hooks/useFetch";
import { useParams } from "react-router-dom";
import { jobKeys } from "components/job/queries";

// import { jobKeys } from "../mutations/queries";

const JobContext = createContext(null);

export const JobProvider = ({ children }) => {
  const { jobId } = useParams();

  const permissions = usePermissions();

  const canRead = permissions.hasCollectionPermission("billables", "read");

  const canReadNextBillingDate = permissions.hasFieldAccess(
    "jobs",
    "read",
    "next_billing_date",
  );

  const {
    data: job,
    isLoading: isJobLoading,
    isError: isJobError,
  } = useFetch({
    queryKey: jobKeys.meta(jobId),
    queryFn: () => fetchJobMeta({ jobId, includeNextBillingDate: canReadNextBillingDate }),
  });

  const { data: milestoneJobCount, isLoading: milestoneJobCountLoading } = useFetch({
    queryKey: milestoneKeys.count(jobId),
    queryFn: () => fetchJobMilestoneCount({ jobId }),
  });

  const { data: billablesJobCount, isLoading: isBillablesJobLoading } = useFetch({
    queryKey: billableKeys.count(jobId),
    queryFn: () => fetchJobBillablesCount({ jobId }),
    enabled: !!canRead,
  });

  const projectCoordinators = [
    job?.project?.coordinator,
    job?.project?.sales_coordinator,
  ];

  const allCoordinators = [...projectCoordinators, job?.coordinator];

  const configCollection = `${job?.type}_job_config`;

  const canReadConfig = permissions.hasFieldAccess(configCollection, "read", "currency");

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useFetch({
    queryKey: jobKeys.config(jobId),
    queryFn: () => {
      if (job.type === "fixed") {
        return fetchFixedJobConfig({ jobId });
      } else if (job.type === "hourly") {
        return fetchHourlyJobConfig({ jobId });
      } else if (job.type === "recurring") {
        return fetchRecurringJobConfig({ jobId });
      } else if (job.type === "dedicated") {
        return fetchDedicatedJobConfig({ jobId });
      } else if (job.type === "bucket") {
        return fetchBucketJobConfig({ jobId });
      }
    },
    enabled: !!job && !!canReadConfig,
  });

  const { data: department, isLoading: isDepartmentLoading } = useFetch({
    queryKey: ["job-department", jobId],
    queryFn: () => fetchJobDepartment({ jobId }),
    enabled: !!jobId,
  });

  const departments = department?.map((dept) => dept.department) || [];



  const isDepartmentRequired = ["fixed"].includes(job?.type);

  return (
    <JobContext.Provider
      value={{
        job,
        ...job,
        config,
        department,
        departments,
        isDepartmentRequired,
        isDepartmentLoading,
        isConfigLoading,
        isConfigError,
        coordinators: allCoordinators,
        projectCoordinators,
        billablesJobCount,
        isBillablesJobLoading,
        isJobLoading,
        milestoneJobCount,
        milestoneJobCountLoading,
        isJobError,
        jobId,
        isLoading:
          isJobLoading ||
          isConfigLoading ||
          milestoneJobCountLoading ||
          isBillablesJobLoading,
      }}
    >
      {children}
    </JobContext.Provider>
  );
};

export const useJob = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error("useJobContext must be used within a JobProvider");
  }
  return context;
};
